import { ArrowRight, Check } from 'lucide-react';
import Link from 'next/link';

import type { PricingCopy } from '@/libs/pricing-i18n';
import type { StripePlan } from '@/types/config';
import { cn } from '@/utils/utils';

import ButtonCheckout from './ButtonCheckout';

export default function PricingCard({
  plan,
  locale,
  copy,
}: {
  plan: StripePlan;
  locale: string;
  copy: PricingCopy['submit'];
}) {
  const planCopy = plan.planCode === 'video_launch' ? copy.videoLaunch : copy.listing;
  const videosHref = locale === 'en' ? '/videos' : `/${locale}/videos`;

  return (
    <article
      className={cn(
        'pricing-card relative flex h-full flex-col overflow-hidden rounded-[18px] border bg-white p-6 sm:p-8',
        plan.isFeatured
          ? 'border-[color:var(--editorial-accent)] shadow-[0_24px_70px_rgba(16,35,63,0.12)] lg:-translate-y-3'
          : 'border-[color:var(--editorial-line)]'
      )}
    >
      <div className="flex min-h-8 items-start justify-between gap-4">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--editorial-muted)]">
          {plan.includesVideo ? copy.distributionAsset : copy.directoryPresence}
        </span>
        {plan.badge && (
          <span className="rounded-full bg-[color:var(--editorial-accent-soft)] px-3 py-1 text-xs font-semibold text-[color:var(--editorial-accent-dark)]">
            {plan.badge}
          </span>
        )}
      </div>

      <div className="mt-6 border-b border-[color:var(--editorial-line)] pb-6">
        <h2 className="font-editorial text-3xl font-semibold tracking-[-0.025em] text-[color:var(--editorial-ink)] sm:text-4xl">
          {planCopy.name}
        </h2>
        <p className="mt-3 max-w-[48ch] text-sm leading-6 text-[color:var(--editorial-muted)] sm:text-base">
          {planCopy.description}
        </p>
        {plan.includesVideo && (
          <Link
            href={videosHref}
            className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--editorial-accent-dark)] underline decoration-[color:var(--editorial-accent)] underline-offset-4 transition-colors hover:text-[color:var(--editorial-ink)]"
          >
            {copy.publishedExamples}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}

        <div className="mt-7 flex flex-wrap items-end gap-x-3 gap-y-1">
          {plan.originalPrice && plan.originalPrice > plan.price && (
            <span className="price-figures mb-2 text-xl font-semibold text-[color:var(--editorial-muted)] line-through decoration-[color:var(--editorial-accent)] decoration-2">
              ${plan.originalPrice.toFixed(2)}
            </span>
          )}
          <span className="price-figures text-5xl font-semibold tracking-[-0.05em] text-[color:var(--editorial-ink)] sm:text-6xl">
            ${plan.price.toFixed(2)}
          </span>
          <span className="mb-2 text-sm font-semibold uppercase tracking-[0.12em] text-[color:var(--editorial-muted)]">
            {copy.usdOnce}
          </span>
        </div>
      </div>

      <ul className="my-7 flex-1 space-y-3.5" aria-label={`${planCopy.name} ${copy.includes}`}>
        {planCopy.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-sm leading-6 text-slate-700">
            <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--editorial-ink)] text-white">
              <Check className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
            </span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <ButtonCheckout planCode={plan.planCode}>
        {plan.includesVideo ? copy.chooseVideo : copy.chooseListing}
        <ArrowRight className="h-5 w-5" aria-hidden="true" />
      </ButtonCheckout>
      <p className="mt-3 text-center text-xs leading-5 text-[color:var(--editorial-muted)]">
        {plan.includesVideo
          ? copy.automaticProduction
          : copy.priorityReview}
      </p>
    </article>
  );
}
